import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  ArrowRight,
  ClipboardList,
  Activity,
  Sparkles,
  ShieldCheck,
  Clock,
  HeartPulse,
  Leaf,
  UserRound,
  Calendar,
  HelpCircle
} from 'lucide-react';
import TestimonialsSection from '../components/TestimonialsSection';
import SEO from '../components/SEO';

const ONEDOC_URL = 'https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid';

const journey = [
  {
    n: '01',
    title: 'Première consultation',
    duration: '~ 1h00',
    text:
      "Recueil détaillé de vos habitudes alimentaires, contexte personnel et médical, antécédents et objectifs. Analyse corporelle par bioimpédance et mesures clés."
  },
  {
    n: '02',
    title: 'Plan personnalisé',
    duration: 'sur mesure',
    text:
      "Mise en place d'un rééquilibrage alimentaire réaliste et adapté à votre mode de vie, installation de l'application Nutrium pour le suivi quotidien."
  },
  {
    n: '03',
    title: 'Suivi & ajustement',
    duration: '~ 30 min',
    text:
      "Consultations de suivi régulières pour ajuster les recommandations, mesurer vos progrès et maintenir une dynamique durable dans le temps."
  }
];

const prestations = [
  {
    icon: Leaf,
    title: 'Consultation nutritionnelle',
    price: '150 CHF',
    duration: '~ 1h00',
    description:
      "Première étape de votre parcours : bilan complet, bioimpédance, mise en place d'un plan alimentaire personnalisé.",
    href: '/prestations/consultation-nutritionnelle',
    testid: 'prestation-consultation'
  },
  {
    icon: UserRound,
    title: 'Santé de la femme',
    price: '150 CHF',
    duration: '~ 1h00',
    description:
      "Nutrition adaptée aux étapes de la vie de la femme : équilibre hormonal, SOPK, fertilité, grossesse, post-partum, ménopause.",
    href: '/prestations/consultation-nutritionnelle',
    testid: 'prestation-sante-femme'
  },
  {
    icon: Sparkles,
    title: "Séance d'hypnopraxie",
    price: '150 CHF',
    duration: '~ 1h00',
    badge: 'Nouveau',
    description:
      "Travail sur le stress, les émotions, les compulsions alimentaires, la confiance en soi et les changements de vie.",
    href: '/prestations/hypnopraxie',
    testid: 'prestation-hypnopraxie'
  },
  {
    icon: Activity,
    title: 'Analyse corporelle & micronutrition',
    price: 'Dès 150 CHF',
    duration: 'variable',
    description:
      "Composition corporelle complète et bilan micronutritionnel pour identifier les déséquilibres et personnaliser l'accompagnement.",
    href: '/prestations/consultation-nutritionnelle',
    testid: 'prestation-analyse'
  },
  {
    icon: ClipboardList,
    title: 'Consultation de suivi',
    price: '100 CHF',
    duration: '~ 30 min',
    description:
      "Suivi régulier pour ajuster votre plan, célébrer vos progrès et maintenir la dynamique sur le long terme.",
    href: '/prestations/consultation-nutritionnelle',
    testid: 'prestation-suivi'
  },
  {
    icon: HeartPulse,
    title: 'Nutrition & santé cardiovasculaire',
    price: '150 CHF',
    duration: '~ 1h00',
    description:
      "Accompagnement nutritionnel en lien avec la santé cardiaque, le cholestérol, l'hypertension et les facteurs de risque.",
    href: '/prestations/consultation-nutritionnelle',
    testid: 'prestation-cardio'
  }
];

const indications = [
  'Perte de poids durable',
  'SOPK (syndrome des ovaires polykystiques)',
  'Troubles hormonaux et déséquilibres',
  'Périménopause & ménopause',
  'Fertilité & projet de grossesse',
  'Post-partum & allaitement',
  'Troubles digestifs',
  'Diabète & cholestérol',
  'Fatigue chronique',
  'Compulsions alimentaires',
  'Rééquilibrage alimentaire',
  'Bien-être et énergie au quotidien'
];

const inclus = [
  'Bilan nutritionnel complet et personnalisé',
  'Analyse corporelle par bioimpédance',
  "Application de suivi Nutrium incluse",
  'Plan alimentaire réaliste et adapté à votre quotidien',
  "Écoute, bienveillance et confidentialité",
  "Conseils pratiques fondés sur des bases scientifiques"
];

const faqs = [
  {
    q: 'Combien coûte une consultation ?',
    a: "La première consultation est à 150 CHF (environ 1h00). Les consultations de suivi sont à 100 CHF (environ 30 min). Certaines prestations spécifiques sont disponibles à partir de 150 CHF."
  },
  {
    q: 'Les consultations sont-elles remboursées ?',
    a: "Oui, les consultations sont remboursées par la plupart des assurances complémentaires grâce à nos agréments ASCA et RME. Renseignez-vous auprès de votre assurance pour connaître les modalités exactes."
  },
  {
    q: 'Comment prendre rendez-vous ?',
    a: "Vous pouvez réserver votre rendez-vous en ligne 24h/24 via OneDoc en quelques clics, ou nous contacter directement par téléphone au 076 779 93 02."
  },
  {
    q: 'Où se déroulent les consultations ?',
    a: "Dans nos deux cabinets sur la Riviera vaudoise : Vevey (Av. du Général-Guisan 26) et Clarens / Montreux (Rue du Grammont 2)."
  }
];

const ConsultationsPage = () => {
  return (
    <div style={{ marginTop: '100px' }} data-testid="consultations-page">
      <SEO
        title="Nos consultations de nutrition à Vevey, Clarens & Montreux | Nutri Centre Riviera"
        description="Consultations de nutrition personnalisées sur la Riviera vaudoise : première consultation (150 CHF), suivi (100 CHF), hypnopraxie, analyse corporelle. Bilan complet, application Nutrium incluse. Remboursement ASCA & RME. Vevey, Clarens, Montreux."
        keywords="consultation nutritionniste Vevey, consultation nutrition Clarens, bilan nutritionnel Montreux, séance hypnopraxie, suivi nutritionnel, prix consultation nutrition Riviera, remboursement ASCA RME, Nutri Centre Riviera"
        url="https://nutricentreriviera.ch/consultations"
      />

      {/* Hero */}
      <section
        style={{
          background: 'var(--gradient-hero)',
          padding: '5rem 0 4rem',
          textAlign: 'center'
        }}
      >
        <div className="container" style={{ maxWidth: '860px' }}>
          <span
            className="caption"
            style={{
              color: 'var(--accent-primary)',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              fontSize: '0.85rem',
              fontWeight: 600,
              marginBottom: '1rem',
              display: 'inline-block'
            }}
          >
            Nos consultations
          </span>
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            Un accompagnement nutritionnel sur mesure
          </h1>
          <p
            className="heading-subtitle"
            style={{ maxWidth: '720px', margin: '0 auto 2.25rem' }}
          >
            Une approche scientifique, humaine et personnalisée, pensée pour vous accompagner durablement
            vers une meilleure santé et un bien-être optimal.
          </p>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}
          >
            <a
              href={ONEDOC_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ textDecoration: 'none' }}
              data-testid="consultations-hero-cta"
            >
              Prendre rendez-vous
              <ArrowRight size={20} />
            </a>
            <a
              href="#prestations"
              className="btn-secondary"
              style={{ textDecoration: 'none' }}
              data-testid="consultations-hero-secondary"
            >
              Découvrir nos prestations
            </a>
          </div>
        </div>
      </section>

      {/* Le déroulement (3 étapes) */}
      <section
        data-testid="consultations-journey"
        style={{ background: 'white', padding: '6rem 0' }}
      >
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{ textAlign: 'center', maxWidth: '780px', margin: '0 auto 3.5rem' }}>
            <span
              className="caption"
              style={{
                color: 'var(--accent-primary)',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                fontSize: '0.85rem',
                fontWeight: 600,
                marginBottom: '1rem',
                display: 'inline-block'
              }}
            >
              Le parcours
            </span>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Comment se déroule un accompagnement
            </h2>
            <p className="body-large" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
              Une démarche claire, progressive et adaptée à votre rythme.
            </p>
          </div>

          <div
            className="journey-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '2rem'
            }}
          >
            {journey.map((step, i) => (
              <div
                key={i}
                data-testid={`journey-step-${i}`}
                className="journey-card"
                style={{
                  background: 'var(--bg-page)',
                  borderRadius: '1.25rem',
                  padding: '2.25rem 1.75rem',
                  border: '1px solid var(--border-light)',
                  transition: 'all 0.3s ease',
                  position: 'relative'
                }}
              >
                <span
                  style={{
                    fontFamily: '"Playfair Display", Georgia, serif',
                    fontSize: '2.75rem',
                    fontWeight: 700,
                    color: 'var(--accent-primary)',
                    opacity: 0.18,
                    position: 'absolute',
                    top: '1rem',
                    right: '1.5rem',
                    lineHeight: 1
                  }}
                >
                  {step.n}
                </span>
                <h3
                  className="heading-3"
                  style={{ marginBottom: '0.5rem', fontSize: '1.3rem' }}
                >
                  {step.title}
                </h3>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    fontSize: '0.85rem',
                    color: 'var(--accent-primary)',
                    fontWeight: 600,
                    marginBottom: '1rem'
                  }}
                >
                  <Clock size={14} />
                  {step.duration}
                </div>
                <p
                  className="body-medium"
                  style={{ color: 'var(--text-muted)', lineHeight: 1.7, margin: 0 }}
                >
                  {step.text}
                </p>
              </div>
            ))}
          </div>

          <style>{`
            .journey-card:hover {
              transform: translateY(-4px);
              border-color: var(--accent-primary);
              box-shadow: 0 12px 30px rgba(61, 84, 70, 0.10);
            }
            @media (max-width: 900px) {
              .journey-grid { grid-template-columns: 1fr !important; }
            }
          `}</style>
        </div>
      </section>

      {/* Prestations détaillées */}
      <section
        id="prestations"
        data-testid="consultations-prestations"
        style={{ background: 'var(--bg-section)', padding: '6rem 0' }}
      >
        <div className="container" style={{ maxWidth: '1280px' }}>
          <div style={{ textAlign: 'center', maxWidth: '780px', margin: '0 auto 3.5rem' }}>
            <span
              className="caption"
              style={{
                color: 'var(--accent-primary)',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                fontSize: '0.85rem',
                fontWeight: 600,
                marginBottom: '1rem',
                display: 'inline-block'
              }}
            >
              Prestations
            </span>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Des consultations adaptées à chaque besoin
            </h2>
            <p className="body-large" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
              Choisissez la prestation qui correspond le mieux à votre situation et à vos objectifs.
            </p>
          </div>

          <div
            className="prestations-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1.5rem'
            }}
          >
            {prestations.map((p, idx) => {
              const Icon = p.icon;
              return (
                <article
                  key={idx}
                  data-testid={p.testid}
                  className="prestation-card"
                  style={{
                    background: 'white',
                    border: '1px solid var(--border-light)',
                    borderRadius: '1.25rem',
                    padding: '2rem 1.75rem',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    position: 'relative'
                  }}
                >
                  {p.badge && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        background: 'var(--accent-primary)',
                        color: 'white',
                        padding: '0.3rem 0.75rem',
                        borderRadius: '999px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        boxShadow: '0 4px 12px rgba(61, 84, 70, 0.25)'
                      }}
                    >
                      {p.badge}
                    </span>
                  )}

                  <div
                    style={{
                      width: '3.25rem',
                      height: '3.25rem',
                      borderRadius: '0.85rem',
                      background: 'var(--accent-primary-light, #E9EEE8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1.25rem'
                    }}
                  >
                    <Icon size={24} strokeWidth={1.75} style={{ color: 'var(--accent-primary)' }} />
                  </div>

                  <h3
                    className="heading-3"
                    style={{ marginBottom: '0.5rem', fontSize: '1.2rem' }}
                  >
                    {p.title}
                  </h3>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      marginBottom: '1rem',
                      flexWrap: 'wrap'
                    }}
                  >
                    <span
                      style={{
                        fontFamily: '"Playfair Display", Georgia, serif',
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        color: 'var(--accent-primary)'
                      }}
                    >
                      {p.price}
                    </span>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        fontSize: '0.82rem',
                        color: 'var(--text-muted)'
                      }}
                    >
                      <Clock size={13} />
                      {p.duration}
                    </span>
                  </div>

                  <p
                    className="body-medium"
                    style={{
                      color: 'var(--text-muted)',
                      lineHeight: 1.65,
                      marginBottom: '1.75rem',
                      flex: 1,
                      fontSize: '0.95rem'
                    }}
                  >
                    {p.description}
                  </p>

                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <Link
                      to={p.href}
                      className="btn-secondary"
                      style={{
                        textDecoration: 'none',
                        fontSize: '0.88rem',
                        padding: '0.6rem 1.1rem',
                        flex: '1 1 auto',
                        justifyContent: 'center',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem'
                      }}
                    >
                      En savoir +
                    </Link>
                    <a
                      href={ONEDOC_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary"
                      style={{
                        textDecoration: 'none',
                        fontSize: '0.88rem',
                        padding: '0.6rem 1.1rem',
                        flex: '1 1 auto',
                        justifyContent: 'center'
                      }}
                    >
                      Réserver
                      <ArrowRight size={16} />
                    </a>
                  </div>
                </article>
              );
            })}
          </div>

          <p
            style={{
              textAlign: 'center',
              marginTop: '2.5rem',
              fontSize: '0.9rem',
              color: 'var(--text-muted)',
              fontStyle: 'italic'
            }}
          >
            * Consultations remboursées par la plupart des assurances complémentaires — agréments ASCA &amp; RME.
          </p>

          <style>{`
            .prestation-card:hover {
              transform: translateY(-6px);
              border-color: var(--accent-primary);
              box-shadow: 0 14px 36px rgba(61, 84, 70, 0.12);
            }
            @media (max-width: 1024px) {
              .prestations-grid { grid-template-columns: repeat(2, 1fr) !important; }
            }
            @media (max-width: 640px) {
              .prestations-grid { grid-template-columns: 1fr !important; }
            }
          `}</style>
        </div>
      </section>

      {/* Pour qui + Ce qui est inclus */}
      <section style={{ background: 'white', padding: '6rem 0' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div
            className="dual-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '3rem',
              alignItems: 'start'
            }}
          >
            {/* Pour qui ? */}
            <div data-testid="consultations-indications">
              <span
                className="caption"
                style={{
                  color: 'var(--accent-primary)',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  marginBottom: '1rem',
                  display: 'inline-block'
                }}
              >
                Pour qui ?
              </span>
              <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
                Les situations que nous accompagnons
              </h2>
              <p
                className="body-large"
                style={{ color: 'var(--text-muted)', marginBottom: '2rem', lineHeight: 1.7 }}
              >
                Quel que soit votre point de départ, nous identifions les causes profondes et mettons en place
                des solutions concrètes et durables.
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.65rem'
                }}
                className="indications-list"
              >
                {indications.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.6rem',
                      fontSize: '0.92rem',
                      lineHeight: 1.5,
                      color: 'var(--text-primary)'
                    }}
                  >
                    <CheckCircle2
                      size={16}
                      strokeWidth={2.25}
                      style={{
                        color: 'var(--accent-primary)',
                        flexShrink: 0,
                        marginTop: '0.2rem'
                      }}
                    />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Ce qui est inclus */}
            <div data-testid="consultations-included">
              <div
                style={{
                  background: 'var(--bg-section)',
                  borderRadius: '1.25rem',
                  padding: '2.5rem',
                  border: '1px solid var(--border-light)',
                  position: 'sticky',
                  top: '120px'
                }}
              >
                <span
                  className="caption"
                  style={{
                    color: 'var(--accent-primary)',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    marginBottom: '1rem',
                    display: 'inline-block'
                  }}
                >
                  Ce qui est inclus
                </span>
                <h2
                  className="heading-2"
                  style={{ marginBottom: '1.5rem', fontSize: '1.6rem' }}
                >
                  Un accompagnement complet
                </h2>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.85rem'
                  }}
                >
                  {inclus.map((item, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        fontSize: '0.95rem',
                        lineHeight: 1.55,
                        color: 'var(--text-primary)'
                      }}
                    >
                      <Sparkles
                        size={18}
                        strokeWidth={1.75}
                        style={{
                          color: 'var(--accent-primary)',
                          flexShrink: 0,
                          marginTop: '0.15rem'
                        }}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                {/* Remboursement bandeau */}
                <div
                  style={{
                    marginTop: '2rem',
                    padding: '1.1rem 1.25rem',
                    background: 'white',
                    border: '1px solid var(--border-light)',
                    borderLeft: '4px solid var(--accent-primary)',
                    borderRadius: '0.75rem',
                    display: 'flex',
                    gap: '0.85rem',
                    alignItems: 'center'
                  }}
                >
                  <ShieldCheck
                    size={26}
                    strokeWidth={1.75}
                    style={{ color: 'var(--accent-primary)', flexShrink: 0 }}
                  />
                  <p
                    className="body-small"
                    style={{
                      margin: 0,
                      lineHeight: 1.55,
                      color: 'var(--text-primary)'
                    }}
                  >
                    Consultations remboursées par les assurances complémentaires — <strong>agréments ASCA &amp; RME</strong>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <style>{`
            @media (max-width: 960px) {
              .dual-grid { grid-template-columns: 1fr !important; }
              .dual-grid > div > div { position: static !important; }
            }
            @media (max-width: 520px) {
              .indications-list { grid-template-columns: 1fr !important; }
            }
          `}</style>
        </div>
      </section>

      {/* FAQ */}
      <section
        data-testid="consultations-faq"
        style={{ background: 'var(--bg-page)', padding: '6rem 0' }}
      >
        <div className="container" style={{ maxWidth: '820px' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span
              className="caption"
              style={{
                color: 'var(--accent-primary)',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                fontSize: '0.85rem',
                fontWeight: 600,
                marginBottom: '1rem',
                display: 'inline-block'
              }}
            >
              Questions fréquentes
            </span>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Vous avez une question ?
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {faqs.map((f, i) => (
              <details
                key={i}
                data-testid={`faq-item-${i}`}
                style={{
                  background: 'white',
                  borderRadius: '1rem',
                  border: '1px solid var(--border-light)',
                  padding: '1.25rem 1.5rem',
                  transition: 'all 0.3s ease'
                }}
              >
                <summary
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    fontSize: '1.05rem',
                    listStyle: 'none'
                  }}
                >
                  <HelpCircle
                    size={20}
                    strokeWidth={1.75}
                    style={{ color: 'var(--accent-primary)', flexShrink: 0 }}
                  />
                  <span>{f.q}</span>
                </summary>
                <p
                  className="body-medium"
                  style={{
                    marginTop: '0.85rem',
                    color: 'var(--text-muted)',
                    lineHeight: 1.7,
                    paddingLeft: '2.75rem'
                  }}
                >
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsSection />

      {/* CTA final */}
      <section
        style={{
          background: 'var(--accent-primary)',
          color: 'white',
          padding: '5rem 0'
        }}
      >
        <div className="container" style={{ textAlign: 'center', maxWidth: '720px' }}>
          <Calendar size={42} strokeWidth={1.5} style={{ marginBottom: '1rem', opacity: 0.92 }} />
          <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
            Prête à commencer votre parcours ?
          </h2>
          <p
            className="body-large"
            style={{
              color: 'white',
              opacity: 0.95,
              marginBottom: '2.5rem',
              lineHeight: 1.7
            }}
          >
            Réservez votre première consultation en quelques clics et faites le premier pas vers
            un accompagnement nutritionnel personnalisé et durable.
          </p>
          <a
            href={ONEDOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{
              background: 'white',
              color: 'var(--accent-primary)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              textDecoration: 'none'
            }}
            data-testid="consultations-final-cta"
          >
            Prendre rendez-vous
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default ConsultationsPage;
